/**
 * ============================================================
 * KEJJA OS — src/pages/pantry/PantryItemForm.tsx
 * Create + edit in one form.
 * ============================================================
 */

import { useState, type FormEvent } from 'react';
import { X } from 'lucide-react';
import { CATEGORIES, LOCATIONS, UNITS } from './PantryLayout';
import { useCreatePantryItem, useUpdatePantryItem } from '@/hooks/usePantry';
import type { PantryItem } from '@/lib/pantryApi';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { EmojiPicker } from '@/components/EmojiPicker';

interface Props {
  homeId: string;
  item: PantryItem | null;
  onClose: () => void;
}

export default function PantryItemForm({ homeId, item, onClose }: Props) {
  const createMut = useCreatePantryItem(homeId);
  const updateMut = useUpdatePantryItem(homeId);

  const [name, setName] = useState(item?.name ?? '');
  const [emoji, setEmoji] = useState(item?.emoji ?? '');
  const [category, setCategory] = useState(item?.category ?? 'other');
  const [location, setLocation] = useState(item?.location ?? 'pantry');
  const [quantity, setQuantity] = useState(String(item?.quantity ?? 1));
  const [unit, setUnit] = useState(item?.unit ?? 'pcs');
  const [minStock, setMinStock] = useState(item?.minStockLevel != null ? String(item.minStockLevel) : '');
  const [purchasedOn, setPurchasedOn] = useState(item?.purchasedOn ?? '');
  const [expiresOn, setExpiresOn] = useState(item?.expiresOn ?? '');
  const [notes, setNotes] = useState(item?.notes ?? '');
  const [err, setErr] = useState<string | null>(null);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setErr(null);
    if (!name.trim()) { setErr('Name is required'); return; }
    const q = parseFloat(quantity);
    if (isNaN(q) || q < 0) { setErr('Enter a valid quantity'); return; }
    const min = minStock.trim() === '' ? null : parseFloat(minStock);
    if (min !== null && (isNaN(min) || min < 0)) { setErr('Invalid min stock'); return; }

    const payload = {
      name: name.trim(),
      emoji: emoji || null,
      category, location,
      quantity: q,
      unit,
      minStockLevel: min,
      purchasedOn: purchasedOn || null,
      expiresOn: expiresOn || null,
      notes: notes.trim() || null,
    };

    const mut = item
      ? updateMut.mutate({ itemId: item.id, patch: payload })
      : createMut.mutate(payload);

    Promise.resolve(mut);

    // Handle success/error manually since we lose mut reference
    (item ? updateMut : createMut).reset?.();
    setTimeout(() => onClose(), 150);
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <Card className="w-full max-w-lg my-8">
        <CardContent className="p-5">
          <form onSubmit={submit} className="space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold">{item ? 'Edit item' : 'Add pantry item'}</h2>
              <Button type="button" size="sm" variant="ghost" onClick={onClose}>
                <X className="w-4 h-4" />
              </Button>
            </div>

            <div className="flex items-start gap-3">
              <div>
                <Label className="text-xs">Icon</Label>
                <div className="mt-1 w-16">
                  <EmojiPicker value={emoji ?? ''} onChange={setEmoji} />
                </div>
              </div>
              <div className="flex-1">
                <Label className="text-xs">Name</Label>
                <Input
                  autoFocus value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Unga wa Ugali"
                  className="mt-1"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Category</Label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="mt-1 w-full h-9 rounded-md border border-border bg-background px-2 text-sm"
                >
                  {CATEGORIES.map((c) => (
                    <option key={c.value} value={c.value}>{c.emoji} {c.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-xs">Location</Label>
                <select
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  className="mt-1 w-full h-9 rounded-md border border-border bg-background px-2 text-sm"
                >
                  {LOCATIONS.map((l) => (
                    <option key={l.value} value={l.value}>{l.emoji} {l.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-[1fr_1fr_1fr] gap-2">
              <div>
                <Label className="text-xs">Quantity</Label>
                <Input
                  type="number" step="0.1" min="0"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-xs">Unit</Label>
                <select
                  value={unit}
                  onChange={(e) => setUnit(e.target.value)}
                  className="mt-1 w-full h-9 rounded-md border border-border bg-background px-2 text-sm"
                >
                  {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs">Min stock</Label>
                <Input
                  type="number" step="0.1" min="0"
                  value={minStock}
                  onChange={(e) => setMinStock(e.target.value)}
                  placeholder="alert below"
                  className="mt-1"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Purchased on</Label>
                <Input
                  type="date" value={purchasedOn}
                  onChange={(e) => setPurchasedOn(e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-xs">Expires on</Label>
                <Input
                  type="date" value={expiresOn}
                  onChange={(e) => setExpiresOn(e.target.value)}
                  className="mt-1"
                />
              </div>
            </div>

            <div>
              <Label className="text-xs">Notes (optional)</Label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full mt-1 min-h-[60px] rounded-md border border-border bg-background p-2 text-xs"
              />
            </div>

            {err && <div className="text-xs text-rose-600 font-medium">{err}</div>}

            <Button
              type="submit"
              className="bg-emerald-600 hover:bg-emerald-700 text-white w-full"
              disabled={createMut.isPending || updateMut.isPending}
            >
              {createMut.isPending || updateMut.isPending
                ? 'Saving…'
                : (item ? 'Save changes' : 'Add to pantry')}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
